import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppLogin from './AppLogin';
import Dashboard from './Dashboard';
import Referrals from './Referrals';
import Account from './Account';
import Payouts from './Payouts';
import Buy from './Buy';
import Kyc from './Kyc';
import AppSignup from './AppSignup';

type TabKey = 'home' | 'buy' | 'network' | 'payouts' | 'account' | 'kyc';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'home', label: 'Home' },
  { key: 'buy', label: 'Buy' },
  { key: 'network', label: 'Network' },
  { key: 'payouts', label: 'Payouts' },
  { key: 'account', label: 'Account' },
];

function TabIcon({ name, active }: { name: TabKey; active: boolean }) {
  const stroke = active ? '#EC4899' : '#8B82A8';
  const p = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'home': return (<svg {...p}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></svg>);
    case 'buy': return (<svg {...p}><circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" /><path d="M2 3h3l2.4 12.4a1 1 0 0 0 1 .8h8.6a1 1 0 0 0 1-.8L21 7H6" /></svg>);
    case 'network': return (<svg {...p}><circle cx="12" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="18" r="3" /><path d="M12 9v2.5M9.6 16 7.8 14.2M14.4 16l1.8-1.8" /></svg>);
    case 'payouts': return (<svg {...p}><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18M16 14h2" /></svg>);
    case 'account': return (<svg {...p}><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.3 3-5.5 7-5.5s7 2.2 7 5.5" /></svg>);
  }
}

export default function AppShell() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState<TabKey>('home');
  const [authView, setAuthView] = useState<'login' | 'signup'>('login');

  useEffect(() => {
    const id = 'acm-app-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id; link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  if (loading) return <div className="fixed inset-0 flex items-center justify-center text-white" style={{ backgroundColor: '#0A0918' }}>Loading…</div>;
  if (!user)
    return authView === 'signup'
      ? <AppSignup onDone={() => setAuthView('login')} onBackToLogin={() => setAuthView('login')} />
      : <AppLogin onSignup={() => setAuthView('signup')} />;

  return (
    <div className="fixed inset-0 flex flex-col text-white"
      style={{
        fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
        backgroundColor: '#0A0918',
        backgroundImage:
          'radial-gradient(at 15% 8%, rgba(139,92,246,0.22) 0, transparent 45%),' +
          'radial-gradient(at 85% 14%, rgba(236,72,153,0.18) 0, transparent 45%),' +
          'radial-gradient(at 50% 92%, rgba(34,211,238,0.10) 0, transparent 45%)',
      }}>
      <main className="flex-1 overflow-y-auto px-5 pb-6" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 18px)' }}>
        {tab === 'home' && <Dashboard onNavigate={setTab} />}
        {tab === 'buy' && <Buy />}
        {tab === 'network' && <Referrals />}
        {tab === 'payouts' && <Payouts />}
        {tab === 'account' && <Account onNavigate={setTab} />}
        {tab === 'kyc' && <Kyc />}
      </main>

      <nav className="flex justify-around border-t"
        style={{ backgroundColor: 'rgba(10,9,24,0.92)', borderColor: 'rgba(255,255,255,0.07)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 6px)', paddingTop: '8px', backdropFilter: 'blur(12px)' }}>
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} className="flex flex-col items-center gap-1 flex-1 py-1">
              <TabIcon name={t.key} active={active} />
              <span className="text-[11px]" style={{ color: active ? '#EC4899' : '#8B82A8' }}>{t.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}